"use client";

import React, { memo } from "react";
import { HStack, VStack, Text, EmptyState, Box, Stack } from "@chakra-ui/react";
import GenericIcon, {
  type GenericIconType,
  type ResponsiveIconSize,
} from "./actions/GenericIcon";
import { iconSizePresets } from "@/shared/config/iconSizePresets";

const CHECKBOX_SPACE_MARGIN_LEFT = "7";

function getEmptyStateSize(
  size: ResponsiveIconSize,
): "sm" | "md" | "lg" {
  if (typeof size !== "string") {
    return "md";
  }

  if (size === "xs") {
    return "sm";
  }

  if (size === "lg") {
    return "lg";
  }

  return "md";
}

function renderEmptyIcon(
  icon: GenericIconType | null,
  size: ResponsiveIconSize,
  iconColor: string | undefined,
  onIconClick: (() => void) | undefined,
) {
  if (!icon) return null;

  if (onIconClick) {
    return (
      <Box onClick={onIconClick} cursor="pointer" display="inline-flex">
        <GenericIcon icon={icon} size={size} color={iconColor} />
      </Box>
    );
  }

  return <GenericIcon icon={icon} size={size} color={iconColor} />;
}

export type GenericEmptyComponentProps = {
  title?: string;
  message?: string;
  icon?: GenericIconType | null;
  iconColor?: string;
  size?: ResponsiveIconSize;
  variant?: "normal" | "inline" | "minimal";
  compensateForCheckboxSpace?: boolean;
  onIconClick?: () => void;
};

function GenericEmptyComponent({
  title = "Content is empty",
  message = "There is currently nothing to display here.",
  icon = "empty",
  iconColor,
  size = "lg",
  variant = "normal",
  compensateForCheckboxSpace = false,
  onIconClick,
}: GenericEmptyComponentProps) {
  const marginLeft = compensateForCheckboxSpace
    ? CHECKBOX_SPACE_MARGIN_LEFT
    : undefined;
  const emptyStateSize = getEmptyStateSize(size);
  const iconElement = renderEmptyIcon(icon, size, iconColor, onIconClick);

  if (variant === "inline") {
    return (
      <Box m="2" ml={marginLeft}>
        <Stack gap="4" justify="center">
          {iconElement}

          <VStack align="start" gap="1">
            <Text fontWeight="semibold" fontSize="sm" lineHeight="short">
              {title}
            </Text>
            <Text fontSize="xs" color="moveon.text.muted" lineHeight="tall">
              {message}
            </Text>
          </VStack>
        </Stack>
      </Box>
    );
  }

  if (variant === "minimal") {
    return (
      <HStack
        align="center"
        borderRadius="md"
        borderWidth="1"
        borderColor="moveon.surface.border"
        bg="moveon.surface.elevatedBg"
        p="2"
        ml={marginLeft}
      >
        {icon ? (
          <GenericIcon
            icon={icon}
            size={iconSizePresets.inline}
            color={iconColor}
          />
        ) : null}
        <Text fontSize="sm" color="moveon.text.muted" lineHeight="tall">
          {title} {message}
        </Text>
      </HStack>
    );
  }

  return (
    <Box m="2" ml={marginLeft}>
      <EmptyState.Root size={emptyStateSize}>
        <EmptyState.Content>
          {iconElement ? (
            <EmptyState.Indicator color={iconColor}>
              {iconElement}
            </EmptyState.Indicator>
          ) : null}
          <VStack textAlign="center" gap="2">
            <EmptyState.Title>{title}</EmptyState.Title>
            <EmptyState.Description>{message}</EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    </Box>
  );
}

export default memo(GenericEmptyComponent);
